import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import MarqueePanel from "@/components/MarqueePanel";
import AdminLayout from "@/components/AdminLayout";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Portfolio from "@/pages/Portfolio";
import PortfolioDetail from "@/pages/PortfolioDetail";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminServices from "@/pages/AdminServices";
import AdminPortfolio from "@/pages/AdminPortfolio";
import AdminAds from "@/pages/AdminAds";
import AdminRestaurants from "@/pages/AdminRestaurants";
import RestaurantDetail from "@/pages/RestaurantDetail";
import AdminPayments from "@/pages/AdminPayments";
import AdminSiteStats from "@/pages/AdminSiteStats";
import AdminSiteContact from "@/pages/AdminSiteContact";
import AdminUsers from "@/pages/AdminUsers";
import AdminAuditLogs from "@/pages/AdminAuditLogs";
import AdminChangePassword from "@/pages/AdminChangePassword";

function PublicShell() {
  const location = useLocation();
  return (
    <>
      <Navbar />
      <MarqueePanel />
      <main key={location.pathname} className="page-transition"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function ProtectedShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-loading">
        <div className="text-[#525252]">Loading...</div>
      </div>
    );
  }
  if (!user || !user.role) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password && location.pathname !== "/admin/change-password") {
    return <Navigate to="/admin/change-password" replace />;
  }
  return <AdminLayout />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
}

function ForcedChangePasswordShell() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#525252]">Loading...</div>;
  if (!user || !user.role) return <Navigate to="/admin/login" replace />;
  return <AdminChangePassword forced={!!user.must_change_password} />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicShell />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:id" element={<PortfolioDetail />} />
              <Route path="/contact" element={<Contact />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/change-password" element={<ForcedChangePasswordShell />} />

            <Route path="/admin" element={<ProtectedShell />}>
              <Route index element={<AdminDashboard />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="portfolio" element={<AdminPortfolio />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="restaurants" element={<AdminOnly><AdminRestaurants /></AdminOnly>} />
              <Route path="restaurants/:id" element={<AdminOnly><RestaurantDetail /></AdminOnly>} />
              <Route path="payments" element={<AdminOnly><AdminPayments /></AdminOnly>} />
              <Route path="site-stats" element={<AdminOnly><AdminSiteStats /></AdminOnly>} />
              <Route path="site-contact" element={<AdminOnly><AdminSiteContact /></AdminOnly>} />
              <Route path="users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
              <Route path="audit-logs" element={<AdminOnly><AdminAuditLogs /></AdminOnly>} />
              <Route path="change-password" element={<AdminChangePassword />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
